import { buildCacheKey, type Dimension, type MeshData } from "@cfg/kernel";
import type { BuildStats, DesignDescription, ValidationIssue } from "@cfg/kernel/worker";
import { createStore } from "zustand/vanilla";

export type ConfiguratorStatus = "idle" | "loading" | "building" | "ready" | "error";

export interface ConfiguratorState {
  key: string | null;
  description: DesignDescription | null;
  params: Record<string, unknown>;
  /** Params the design was opened with (saved config or defaults); used for `dirty`. */
  baseline: Record<string, unknown>;
  mesh: MeshData | null;
  stats: BuildStats | null;
  issues: ValidationIssue[];
  /** Dimension lines for `mesh`, from the design's `dimensions()`; empty when it has none. */
  dimensions: Dimension[];
  status: ConfiguratorStatus;
  error: string | null;
  buildCount: number;
  /** Which param the user last touched, for the viewport to highlight its label. */
  activeParam: string | null;
  /** Param whose slider is being dragged right now (pointer down), or null. */
  dragParam: string | null;
  /** Cache key of the params the current `mesh`/`issues` were built from; compare with `paramsKey()` to tell stale. */
  builtKey: string | null;
  /** Latest request to reveal + focus a field (`useFocusParam`); `n` bumps so repeats re-fire. */
  focusRequest: { name: string; n: number } | null;

  setParams: (patch: Record<string, unknown>) => void;
  replaceParams: (params: Record<string, unknown>, asBaseline?: boolean) => void;
  reset: () => void;
  applyPreset: (id: string) => void;
  setActiveParam: (name: string | null) => void;
  setDragParam: (name: string | null) => void;
  requestFocus: (name: string) => void;
  markSaved: () => void;
}

export type ConfiguratorStore = ReturnType<typeof createConfiguratorStore>;

export function createConfiguratorStore() {
  return createStore<ConfiguratorState>((set, get) => ({
    key: null,
    description: null,
    params: {},
    baseline: {},
    mesh: null,
    stats: null,
    issues: [],
    dimensions: [],
    status: "idle",
    error: null,
    buildCount: 0,
    activeParam: null,
    dragParam: null,
    builtKey: null,
    focusRequest: null,

    setParams: (patch) => set((s) => ({ params: { ...s.params, ...patch } })),
    replaceParams: (params, asBaseline = false) => set(asBaseline ? { params, baseline: params } : { params }),
    reset: () => {
      const d = get().description;
      if (d) set({ params: { ...d.defaults } });
    },
    applyPreset: (id) => {
      const d = get().description;
      const presets = (d?.ui?.presets ?? []) as { id: string; params: Record<string, unknown> }[];
      const preset = presets.find((p) => p.id === id);
      if (d && preset) set({ params: { ...d.defaults, ...preset.params } });
    },
    setActiveParam: (name) => set({ activeParam: name }),
    setDragParam: (name) => set(name ? { dragParam: name, activeParam: name } : { dragParam: null }),
    requestFocus: (name) => set((s) => ({ activeParam: name, focusRequest: { name, n: (s.focusRequest?.n ?? 0) + 1 } })),
    markSaved: () => set((s) => ({ baseline: s.params })),
  }));
}

/** Cache key of the current params (same key the provider builds and caches under). */
export function paramsKey(s: Pick<ConfiguratorState, "description" | "params">): string | null {
  return s.description ? buildCacheKey(s.description.id, s.description.version, s.params, "preview") : null;
}

/** The mesh/issues on screen were built from the current params and nothing is in flight. */
export function isSettled(s: ConfiguratorState): boolean {
  return s.status === "ready" && s.builtKey !== null && s.builtKey === paramsKey(s);
}

/**
 * Resolve with the state once the preview matches the current params (so `issues` are
 * current); reject if the build errors. Used to let a click during a rebuild wait for it.
 */
export function waitForSettled(store: ConfiguratorStore, timeoutMs = 60_000): Promise<ConfiguratorState> {
  return new Promise((resolve, reject) => {
    const check = (s: ConfiguratorState) => {
      if (s.status === "error") return done(() => reject(new Error(s.error ?? "Build failed")));
      if (isSettled(s)) return done(() => resolve(s));
    };
    let unsub = () => {};
    const timer = setTimeout(() => done(() => reject(new Error("Timed out waiting for the preview"))), timeoutMs);
    let finished = false;
    const done = (fn: () => void) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      unsub();
      fn();
    };
    unsub = store.subscribe(check);
    check(store.getState());
  });
}

export function isDirty(s: Pick<ConfiguratorState, "params" | "baseline">): boolean {
  const keys = new Set([...Object.keys(s.params), ...Object.keys(s.baseline)]);
  for (const k of keys) if (s.params[k] !== s.baseline[k]) return true;
  return false;
}
